import { useEffect } from 'react';
import { useSelector } from 'react-redux';

import {
  getExpenses,
  getNetMonthlyRentalIncome,
} from 'app/pages/loanApplication/LoanApplication.utils';

import { rentCalculatedPurposeOptions } from 'constants/options';
import { PendingChangesActionType } from 'constants/pendingChanges';

import { selectCommonInfoLoanApplication } from 'store/loanApplications/selectors';

import { ENTITY_TYPE_HREF_MAP } from '../../../../EditModal.constants';
import { proposedPropertyExpensesFields } from '../PropertyExpenses.config';
import {
  checkIsAmountOfRentCollectedActive,
  getMonthlyPropertyTaxes,
} from '../PropertyExpenses.utils';

const usePropertyExpensesCalculations = ({
  form,
  applicationData,
  entitySideData,
  setInForm,
  setMultipleInForm,
  subtab,
}) => {
  const { loanPurpose } = useSelector(selectCommonInfoLoanApplication);

  const formProposedPropertyExpenses = form.proposedPropertyExpenses || {};
  const isPropertyExpensesMounted = 'principalAndInterest' in formProposedPropertyExpenses;
  const entityProposedPropertyExpenses = isPropertyExpensesMounted
    ? formProposedPropertyExpenses
    : applicationData.proposedPropertyExpenses || {};
  const { otherExpenses, propertyTaxes, supplementalInsurances } = isPropertyExpensesMounted
    ? entitySideData
    : applicationData.proposedPropertyExpenses || {};
  const existingOtherExpenses = otherExpenses?.filter(
    ({ pendingChangesActionType }) => pendingChangesActionType !== PendingChangesActionType.DELETED,
  );
  const { principalAndInterest } = entityProposedPropertyExpenses;
  const { firstMortgage, secondMortgage, taxesInsuranceEscrowed } =
    applicationData.proposedPropertyExpenses || {};
  const occupancy = form.occupancy?.value || applicationData.occupancy;
  const propertyType = form.propertyType?.value || applicationData.propertyType;
  const rentCalculatedBasedOn =
    form.proposedPropertyExpenses?.rentCalculatedBasedOn?.value ||
    applicationData.proposedPropertyExpenses?.rentCalculatedBasedOn;
  const status = form.status?.value || applicationData.status;

  const monthlyPropertyTaxes = getMonthlyPropertyTaxes({
    propertyTaxes,
    taxesInsuranceEscrowed,
  });

  const expenses = getExpenses({
    monthlyPropertyTaxes,
    monthlyHomeownersInsurance: entityProposedPropertyExpenses.monthlyHomeownersInsurance,
    monthlyPmi: entityProposedPropertyExpenses.pmi,
    hasOtherExpenses: existingOtherExpenses?.length,
    associationFees: !!entityProposedPropertyExpenses.associationFeesAmount,
    associationFeesAmount: entityProposedPropertyExpenses.associationFeesAmount,
    isSubjectProperty: true,
    otherExpenses: existingOtherExpenses,
    supplementalInsurances,
  });

  const netMonthlyRentalIncome = getNetMonthlyRentalIncome({
    monthlyRentalIncome: entityProposedPropertyExpenses.monthlyRentalIncome,
    rentCalculatedBasedOn: entityProposedPropertyExpenses.rentCalculatedBasedOn,
    status,
    isSubjectProperty: true,
    loanPurpose,
    principalAndInterest,
    expenses,
    liabilities: [firstMortgage, secondMortgage],
    occupancy,
    propertyType,
  });

  const mortgagesPayment = principalAndInterest;

  const isAmountOfRentCollectedActive = checkIsAmountOfRentCollectedActive({
    occupancy,
    propertyType,
  });

  useEffect(() => {
    if (subtab !== ENTITY_TYPE_HREF_MAP.PROPERTY_INFO || !isPropertyExpensesMounted) {
      return;
    }

    setInForm('proposedPropertyExpenses.isRentalIncome', isAmountOfRentCollectedActive, false);
  }, [isAmountOfRentCollectedActive, isPropertyExpensesMounted, subtab, setInForm]);

  useEffect(() => {
    if (
      subtab !== ENTITY_TYPE_HREF_MAP.PROPERTY_INFO ||
      !isPropertyExpensesMounted ||
      !isAmountOfRentCollectedActive
    ) {
      return;
    }

    setMultipleInForm(
      {
        [proposedPropertyExpensesFields.expenses.nameInModel]: expenses,
        [proposedPropertyExpensesFields.netMonthlyRentalIncome.nameInModel]: netMonthlyRentalIncome,
        [proposedPropertyExpensesFields.mortgagesPayment.nameInModel]: mortgagesPayment,
      },
      false,
    );

    if (!rentCalculatedBasedOn) {
      setInForm(
        proposedPropertyExpensesFields.rentCalculatedBasedOn.nameInModel,
        rentCalculatedPurposeOptions[0].value,
      );
    }
  }, [
    expenses,
    isAmountOfRentCollectedActive,
    isPropertyExpensesMounted,
    mortgagesPayment,
    netMonthlyRentalIncome,
    rentCalculatedBasedOn,
    subtab,
    setInForm,
    setMultipleInForm,
  ]);
};

export default usePropertyExpensesCalculations;
